import { FFmpeg } from '@ffmpeg/ffmpeg';
import type Konva from 'konva';
import { useCanvasStore } from '../store/canvasStore';
import type { SelectionType } from '../types';

interface GifExportOptions {
  duration?: number;
  framesPerSecond?: number;
  quality?: number;
}

export class GifGenerator {
  private stage: Konva.Stage;
  private options: Required<GifExportOptions>;
  private loadingElement: HTMLDivElement | null = null;
  private originalState: {
    previewMode: boolean;
    selectedId: SelectionType;
    isEditingTextId: string | null;
  };
  private transformer: Konva.Transformer | null;
  private originalTransformerVisibility: boolean | undefined;
  private originalNodeStyles: Array<{ id: string; stroke?: string; strokeWidth?: number; className?: string }> = [];

  constructor(stage: Konva.Stage, options?: GifExportOptions) {
    this.stage = stage;

    this.options = {
      duration: options?.duration || 5,
      framesPerSecond: options?.framesPerSecond || 24,
      quality: options?.quality || 2.0,
    };

    const canvasStore = useCanvasStore.getState();
    this.originalState = {
      previewMode: canvasStore.previewMode,
      selectedId: canvasStore.selectedId,
      isEditingTextId: canvasStore.isEditingTextId,
    };

    this.transformer = canvasStore.trRef.current as unknown as Konva.Transformer;
    this.originalTransformerVisibility = this.transformer?.visible();
  }

  prepareStageForExport(): void {
    try {
      this.loadingElement = document.createElement('div');
      this.loadingElement.className = 'canvas-loading-overlay';
      this.loadingElement.innerHTML = `
        <div class="canvas-loading-spinner"></div>
        <div class="canvas-loading-text">Preparing animation...</div>
      `;
      document.body.appendChild(this.loadingElement);

      if (this.transformer) {
        this.transformer.visible(false);
      }

      const nodes = this.stage.find('Text, Image');

      nodes.forEach((node) => {
        try {
          this.originalNodeStyles.push({
            id: node.id(),
            stroke: node.getAttr('stroke'),
            strokeWidth: node.getAttr('strokeWidth'),
            className: node.getAttr('className'),
          });

          node.setAttr('strokeEnabled', false);
          node.setAttr('stroke', undefined);
          node.setAttr('strokeWidth', 0);
          node.setAttr('className', '');
        } catch (nodeError) {}
      });

      this.stage.batchDraw();

      useCanvasStore.setState({
        selectedId: null,
        isEditingTextId: null,
        previewMode: false,
      });
    } catch (error) {}
  }

  async captureFrames(): Promise<string[]> {
    const startTime = performance.now();
    try {
      this.updateLoadingStatus('Starting animation capture...');
      this.normalizeNodePadding();

      await new Promise<void>((resolve) => setTimeout(resolve, 75));

      useCanvasStore.setState({
        previewMode: true,
      });

      await new Promise<void>((resolve) => setTimeout(resolve, 75));

      const totalFrames = this.options.framesPerSecond * this.options.duration;
      const frameDelay = Math.floor(1000 / this.options.framesPerSecond);

      const frames: string[] = [];

      return new Promise<string[]>((resolve) => {
        let frameCount = 0;
        let lastFrameTime = 0;

        const captureFrame = (timestamp: number) => {
          try {
            const elapsed = timestamp - lastFrameTime;

            if (elapsed >= frameDelay || frameCount === 0) {
              lastFrameTime = timestamp;

              this.stage.batchDraw();

              const dataURL = this.stage.toDataURL({
                pixelRatio: this.options.quality,
                mimeType: 'image/png',
              });
              frames.push(dataURL);

              const progressPercent = Math.round((frames.length / totalFrames) * 100);
              this.updateLoadingStatus(`Capturing animation: ${progressPercent}%`);

              frameCount++;
            }

            if (frames.length < totalFrames) {
              requestAnimationFrame(captureFrame);
            } else {
              useCanvasStore.setState({ previewMode: false });
              resolve(frames);
            }
          } catch (frameError) {
            useCanvasStore.setState({ previewMode: false });
            resolve(frames);
          }
        };

        requestAnimationFrame(captureFrame);
      });
    } catch (error) {
      return [];
    } finally {
      const duration = (performance.now() - startTime) / 1000;
    }
  }

  async processFrames(frames: string[], download: boolean): Promise<Blob | null> {
    const startTime = performance.now();
    this.updateLoadingStatus('Processing frames...');

    try {
      const ffmpeg = new FFmpeg();
      await ffmpeg.load();

      for (let i = 0; i < frames.length; i++) {
        try {
          const filename = `frame_${i.toString().padStart(4, '0')}.png`;
          const res = await fetch(frames[i]);
          const blob = await res.blob();
          const buffer = await blob.arrayBuffer();
          await ffmpeg.writeFile(filename, new Uint8Array(buffer));

          if (i % 5 === 0) {
            const progressPercent = Math.round((i / frames.length) * 100);
            this.updateLoadingStatus(`Processing frames: ${progressPercent}%`);
          }
        } catch (error) {
          if (i > frames.length / 2) {
            break;
          }
          throw error;
        }
      }

      this.updateLoadingStatus('Creating GIF...');
      const execStartTime = performance.now();

      await ffmpeg.exec([
        '-framerate',
        this.options.framesPerSecond.toString(),
        '-i',
        'frame_%04d.png',
        '-vf',
        'split[s0][s1];[s0]palettegen=max_colors=256:reserve_transparent=0[p];[s1][p]paletteuse=dither=sierra2_4a',
        '-loop',
        '0',
        '-y',
        'output.gif',
      ]);

      let fileData: ArrayBufferLike;
      const rawFileData = await ffmpeg.readFile('output.gif');
      if (rawFileData instanceof Uint8Array) {
        fileData = rawFileData.buffer;
      } else {
        fileData = await new Blob([rawFileData]).arrayBuffer();
      }

      const gifBlob = new Blob([new Uint8Array(fileData)], { type: 'image/gif' });

      if (gifBlob.size === 0) {
        return null;
      }

      if (download) {
        const url = URL.createObjectURL(gifBlob);
        const link = document.createElement('a');
        link.download = `canvas-animation-${new Date().toISOString().slice(0, 10)}.gif`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      return gifBlob;
    } catch (error) {
      return null;
    } finally {
      const duration = (performance.now() - startTime) / 1000;
    }
  }

  restoreStage(): void {
    this.originalNodeStyles.forEach((style) => {
      const node = this.stage.findOne(`#${style.id}`);
      if (node) {
        if (style.stroke) node.setAttr('stroke', style.stroke);
        if (style.strokeWidth) node.setAttr('strokeWidth', style.strokeWidth);
        if (style.className) node.setAttr('className', style.className);
      }
    });

    if (this.transformer && this.originalTransformerVisibility !== undefined) {
      this.transformer.visible(this.originalTransformerVisibility);
    }

    if (this.loadingElement) {
      document.body.removeChild(this.loadingElement);
      this.loadingElement = null;
    }

    useCanvasStore.setState({
      previewMode: this.originalState.previewMode,
      selectedId: this.originalState.selectedId,
      isEditingTextId: this.originalState.isEditingTextId,
    });

    this.stage.batchDraw();
  }

  private normalizeNodePadding(): void {
    try {
      const allTextNodes = this.stage.find('Text');

      allTextNodes.forEach((node) => {
        try {
          const textNode = node as Konva.Text;
          textNode.padding(0);
          textNode.offsetX(0);
          textNode.offsetY(0);
        } catch (nodeError) {}
      });

      const allImageNodes = this.stage.find('Image');

      allImageNodes.forEach((node) => {
        try {
          if (node.id() !== 'canvas-background-image') {
            node.offsetX(0);
            node.offsetY(0);
          }
        } catch (nodeError) {}
      });

      this.stage.batchDraw();
    } catch (error) {}
  }

  private updateLoadingStatus(message: string): void {
    try {
      if (this.loadingElement) {
        this.loadingElement.innerHTML = `
          <div class="canvas-loading-spinner"></div>
          <div class="canvas-loading-text">${message}</div>
        `;
      }
    } catch (error) {}
  }

  public async generateGif(download = false): Promise<Blob | null> {
    const startTime = performance.now();
    this.prepareStageForExport();

    try {
      const frames = await this.captureFrames();
      return await this.processFrames(frames, download);
    } finally {
      this.restoreStage();
      const duration = (performance.now() - startTime) / 1000;
    }
  }
}
